import { useContext } from "react";
import { ToastContext } from "../context/ToastContext";
import { UserGroupContract } from "../models/UserGroupContract";
import UserGroupContractService from "../services/crud/UserGroupContractService";
import { OverlayContext } from "../context/OverlayContext";
import { Form } from "antd";
import { NewUserGroupContractForm } from "../components/forms/NewUserGroupContractForm";
import moment, { Moment } from "moment";
import { dateStartOfLocalDay } from "../utils/dates";
import { EditUserGroupContractForm } from "../components/forms/EditUserGroupContractForm";
import { AccountBookFilled } from "@ant-design/icons";

export function useUserGroupContractEditModal({
  itemName,
  onActionComplete,
}: {
  itemName: string,
  onActionComplete: Function
}) {
  const [newForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const { loadMessage } = useContext(ToastContext);
  const { openModal, closeModal } = useContext(OverlayContext);

  const openCreateUserGroupContract = ({
    accountId, // For account-admin auth
    userGroupId,
    userGroupLabel,
    lastContractId,
    lastContractMembers,
    lastContractStartDate,
    onAfterSave,
  }: {
    accountId: number,
    userGroupId: number,
    userGroupLabel: string,
    lastContractId: number,
    lastContractMembers: string[],
    lastContractStartDate?: Date,
    onAfterSave?: (addedContract: UserGroupContract) => Promise<void>,
  }) => {
    const terminateContractId = lastContractId;
    const contractsPromise = UserGroupContractService.listUserGroupContractsOnUserGroup(userGroupId);
    openModal({
      title: `${userGroupLabel} - Registrera ${lastContractId !== null ? 'omflytt' : 'första inflytt'}`,
      content: <NewUserGroupContractForm
        form={newForm}
        userGroupId={userGroupId}
        terminateContractId={terminateContractId}
        terminateMembers={lastContractMembers}
        terminateDate={
          terminateContractId !== null ? (
            lastContractStartDate !== null
              ? moment(lastContractStartDate).add(1, "day").startOf("day")
              : moment().add(1, "day").startOf("day")
          ): null
        }
      />,
      onOk: async () => {
        const values = await newForm.validateFields();
        const contracts = await contractsPromise;
        await loadMessage('Saving', async () => {
          if (lastContractId !== null) {
            const contractUpdate = {
              accountId: accountId, // for account-admin auth
              id: lastContractId,
              endDate: values.terminateDate,
            }
            await UserGroupContractService.editUserGroupContract(lastContractId, contractUpdate);
          }

          let startDate = values.newContractStartDate?.toDate() ?? null;
          if (startDate !== null) {
            startDate = dateStartOfLocalDay(startDate);
          }
          const newContract = {
            accountId: accountId, // for account-admin auth
            userGroupId: userGroupId,
            label: `${(contracts?.length ?? 0) + 1}`,
            startDate: startDate,
            members: values.newContractMembers?.map((email) => ({
              username: email,
              startDate: startDate,
              endDate: null,
            })),
          }
          const addedContract = await UserGroupContractService.addUserGroupContract(newContract);
          if (onAfterSave !== undefined) {
            await onAfterSave(addedContract);
          }
          closeModal();
          onActionComplete();
        });
      },
    });
  };
  const openEditUserGroupContract = ({
    accountId, // For account-admin auth
    userGroupLabel,
    userGroupContract,
    onAfterSave,
  }: {
    accountId: number,
    userGroupLabel: string,
    userGroupContract: UserGroupContract,
    onAfterSave?: (addedContract: UserGroupContract) => Promise<void>,
  }) => {
    const updateContractId = userGroupContract.id;
    console.log('mem', userGroupContract.members);
    openModal({
      title: `${userGroupLabel} - Redigera pågående kontrakt`,
      content: <EditUserGroupContractForm
        form={editForm}
        userGroupContract={userGroupContract}
      />,
      onOk: async () => {
        const values = await editForm.validateFields();
        const updateContract = {
          ...values,
          accountId: accountId, // for account-admin auth
          members: values.members?.map((m) => ({
            username: m.username,
            startDate: (m.startDate as any as Moment)?.toDate() ?? null,
            endDate: (m.endDate as any as Moment)?.toDate() ?? null,
          }))
        }
        loadMessage(`Adding ${itemName.toLocaleLowerCase()}...`, async () => {
          const contract = await UserGroupContractService.editUserGroupContract(updateContractId, updateContract);
          if (onAfterSave !== undefined) {
            await onAfterSave(contract);
          }
          closeModal();
          onActionComplete();
        });
      },
    });
  };

  return {openCreateUserGroupContract, openEditUserGroupContract};
}
